import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link, useLocation } from "react-router-dom";
import { Redirect, useParams } from "react-router";

import Loading from "components/ui-components/loading";

import { useHttp } from "hooks/useHttp";

import { Maybe } from "helpers/maybeFunctor";
import formatTime from "helpers/formatTime";
import {
  addPermissionIsUser,
  setAllPermission,
  setCompanyPermission,
  setSidebarStatus,
} from "actions/user";

import { getCompanyPermissionByID } from "api/permission/getPermissions";
import {
  copyUserPermissionFromRole,
  editUserPermission,
  editBulkPermission,
} from "api/permission/editPermissions";
import addNotification from "helpers/notify";
import Loader from "components/Loader";
import { all_permissionsRef, company_permissionsRef, user_permissionsRef, get, globalPut } from "api";

export default function SuperAdminPermissions() {
  const { idCompany } = useParams();
  const dispatch = useDispatch();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);

  const [inputSearchValue, setInputSearchValue] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Package-level toggle state — separate from the granular permission
  // grid below. Saving here calls PUT /super-admin-panel/companies/:id/packages
  // which rewrites the company's Permission row to the union of the
  // selected packages' permission arrays. The granular grid then
  // updates on the next fetch.
  const [allPackages, setAllPackages] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [packagesDirty, setPackagesDirty] = useState(false);
  const [savingPackages, setSavingPackages] = useState(false);
  // Connector platform allowlist — which social platforms this company's
  // Connectors may operate on at all. Default-disabled except linkedin+facebook.
  const [platformCatalog, setPlatformCatalog] = useState([]);
  const [allowedPlatforms, setAllowedPlatforms] = useState([]);
  const [platformsDirty, setPlatformsDirty] = useState(false);
  const [savingPlatforms, setSavingPlatforms] = useState(false);
  // Stripe subscription panel — super-admin-only view of the company's
  // current subscription state, pulled from /stripe/subscription/:companyId.
  const [stripeSub, setStripeSub] = useState(null);
  const [stripeBusy, setStripeBusy] = useState(false);

  const { loading: initialLoading, request: getCompanyPermissionAPI } = useHttp(
    {
      requestCallback: (data) =>
        getCompanyPermissionByID({
          userId: userInfoRedux?.id,
          companyId: idCompany,
        }),
      onLoad: (res) => {
        // setting self permissions to local storage
        if (idCompany === userInfoRedux?.company) {
          console.log("self");
          localStorage.setItem(user_permissionsRef, res?.user_permissions);
          localStorage.setItem(company_permissionsRef, res?.company_permissions);
          localStorage.setItem(all_permissionsRef, res?.all_permissions);
          //
          dispatch(addPermissionIsUser(res?.user_permissions));
          dispatch(setCompanyPermission(res?.company_permissions));
          dispatch(setAllPermission(res?.all_permissions));
        }
        const data = allPermissionRedux?.map((permission) => {
          return {
            permission,
            checked: res?.company_permissions?.includes(permission),
          };
        });
        setPermissions(data);
        setRolePermissions(
          res?.company_permissions ? res?.company_permissions : []
        );
      },
      onError: (res) => {},
    }
  );

  useEffect(() => {
    getCompanyPermissionAPI();
  }, []);

  // Fetch the package list + the company's currently-active set from
  // the super-admin-panel endpoint. Active packages are derived
  // server-side from the company's Permission row (intersection of
  // each package's perms vs. the company's perms).
  useEffect(() => {
    (async () => {
      try {
        const r = await get('/super-admin-panel/companies');
        const d = (r && r.data) || null;
        if (!d) return;
        setAllPackages(d.allPackages || []);
        setPlatformCatalog(d.platformCatalog || []);
        const me = (d.companies || []).find((c) => String(c._id) === String(idCompany));
        const active = (me && me.activePackages) || [];
        setSelectedPackages(active);
        setPackagesDirty(false);
        setAllowedPlatforms((me && me.connectorPlatforms) || []);
        setPlatformsDirty(false);
      } catch (_) { /* leave empty */ }
    })();
  }, [idCompany]);

  const togglePackage = (pkg) => {
    setSelectedPackages((p) => {
      const next = p.includes(pkg) ? p.filter((x) => x !== pkg) : [...p, pkg];
      setPackagesDirty(true);
      return next;
    });
  };

  const togglePlatform = (plat) => {
    setAllowedPlatforms((p) => {
      const next = p.includes(plat) ? p.filter((x) => x !== plat) : [...p, plat];
      setPlatformsDirty(true);
      return next;
    });
  };

  const savePlatforms = async () => {
    setSavingPlatforms(true);
    try {
      await globalPut(`/super-admin-panel/companies/${idCompany}/connector-platforms`, { platforms: allowedPlatforms });
      addNotification('Connector platforms updated for this company.', 'success');
      setPlatformsDirty(false);
    } catch (e) {
      addNotification(e?.message || 'Failed to save connector platforms', 'danger');
    } finally { setSavingPlatforms(false); }
  };

  // Pull Stripe subscription state for the per-company card.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStripeBusy(true);
      try {
        const r = await get(`/stripe/subscription/${idCompany}`);
        if (!cancelled) setStripeSub((r && r.data) || null);
      } catch (_) { if (!cancelled) setStripeSub(null); }
      finally { if (!cancelled) setStripeBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [idCompany]);

  const savePackages = async () => {
    setSavingPackages(true);
    try {
      await globalPut(`/super-admin-panel/companies/${idCompany}/packages`, { packages: selectedPackages });
      addNotification('Packages updated — granular grid below reflects the new permission union.', 'success');
      setPackagesDirty(false);
      // Reload the granular grid so the checkboxes reflect the new state.
      getCompanyPermissionAPI();
    } catch (e) {
      addNotification(e?.message || 'Failed to save packages', 'danger');
    } finally { setSavingPackages(false); }
  };

  const handleCheckBox = async (e) => {
    const data = permissions?.map((permission) => {
      if (permission.permission === e.target.value) {
        return {
          ...permission,
          checked: e.target.checked,
        };
      } else {
        return permission;
      }
    });
    let permissionData = [];
    if (e.target.checked) {
      permissionData = [...rolePermissions, e.target.value];
    } else {
      permissionData = rolePermissions?.filter((rp) => rp !== e.target.value);
    }
    setRolePermissions(permissionData);
    setPermissions(data);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await editUserPermission(idCompany, {
        permissions: rolePermissions,
        entityId: idCompany,
        entityType: "company",
      });
      if (res) {
        setLoading(false);
        addNotification("Permission Updated Successfully", "success");
        getCompanyPermissionAPI();
      }
    } catch (err) {
      console.log("err-------", err);
    }
  };

  return (
    <div className="personal-area">
      {loading || (initialLoading && <Loader />)}

      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <div
              onClick={() => dispatch(setSidebarStatus(true))}
              className="cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
              >
                <path
                  d="M6.84102 8.825L3.02435 5L6.84102 1.175L5.66602 0L0.666016 5L5.66602 10L6.84102 8.825Z"
                  fill="#9E9E9E"
                />
              </svg>
            </div>
            <Link
              to={"/company-admin/projects-settings"}
              className="my-heading f-14 ml-3 mr-3 headerLink"
            >
              Admin
            </Link>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="10"
              viewBox="0 0 7 10"
              fill="none"
            >
              <path
                d="M6.84102 8.825L3.02435 5L6.84102 1.175L5.66602 0L0.666016 5L5.66602 10L6.84102 8.825Z"
                fill="#9E9E9E"
              />
            </svg>
            <Link
              to={"/super-admin"}
              className="my-heading f-14 ml-3 mr-3 headerLink"
            >
              All Companies
            </Link>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="10"
              viewBox="0 0 7 10"
              fill="none"
            >
              <path
                d="M6.84102 8.825L3.02435 5L6.84102 1.175L5.66602 0L0.666016 5L5.66602 10L6.84102 8.825Z"
                fill="#9E9E9E"
              />
            </svg>
            <p className="my-heading f-14 ml-3 mr-3">{}</p>
          </div>
          <div
            className="projActionButtons d-flex flex-wrap"
            style={{ maxWidth: "100vw" }}
          >
            <input
              type="text"
              className="search-bar"
              value={inputSearchValue}
              onChange={(e) => setInputSearchValue(e.target.value)}
              placeholder="Search"
              // disabled={loading}
            />
            <div className="vr mx-3"></div>
            <div className="btn-tool-group">
              <button
                onClick={() => getCompanyPermissionAPI(idCompany)}
                className="px-3 bg-white text-bid shadow-hover"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="17"
                  viewBox="0 0 18 17"
                  fill="none"
                >
                  <path
                    d="M3.55149 2.19418C5.01232 0.931059 6.9166 0.166992 8.99935 0.166992C13.6017 0.166992 17.3327 3.89795 17.3327 8.50033C17.3327 10.2804 16.7745 11.9302 15.8237 13.2842L13.166 8.50033H15.666C15.666 4.81843 12.6813 1.83366 8.99935 1.83366C7.20753 1.83366 5.58082 2.54055 4.38287 3.69068L3.55149 2.19418ZM14.4472 14.8065C12.9863 16.0696 11.0821 16.8337 8.99935 16.8337C4.39697 16.8337 0.666016 13.1027 0.666016 8.50033C0.666016 6.72021 1.22417 5.07046 2.17502 3.71653L4.83268 8.50033H2.33268C2.33268 12.1822 5.31745 15.167 8.99935 15.167C10.7912 15.167 12.4179 14.4601 13.6159 13.31L14.4472 14.8065Z"
                    fill="#3A5765"
                  />
                  <path
                    d="M3.55149 2.19418C5.01232 0.931059 6.9166 0.166992 8.99935 0.166992C13.6017 0.166992 17.3327 3.89795 17.3327 8.50033C17.3327 10.2804 16.7745 11.9302 15.8237 13.2842L13.166 8.50033H15.666C15.666 4.81843 12.6813 1.83366 8.99935 1.83366C7.20753 1.83366 5.58082 2.54055 4.38287 3.69068L3.55149 2.19418ZM14.4472 14.8065C12.9863 16.0696 11.0821 16.8337 8.99935 16.8337C4.39697 16.8337 0.666016 13.1027 0.666016 8.50033C0.666016 6.72021 1.22417 5.07046 2.17502 3.71653L4.83268 8.50033H2.33268C2.33268 12.1822 5.31745 15.167 8.99935 15.167C10.7912 15.167 12.4179 14.4601 13.6159 13.31L14.4472 14.8065Z"
                    fill="black"
                    fillOpacity="0.2"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="content-bg p-2">
        {/* STRIPE SUBSCRIPTION — super-admin's read-only window into the
            company's billing state. Pulls /stripe/subscription/:companyId
            (live Stripe API), shows status + period end + which packages
            the current line items map to. The webhook handlers on
            customer.subscription.{created,updated,deleted} auto-sync
            those packages to the Licensed Packages row below, so this
            card is the audit view (not where you edit billing). */}
        {stripeSub && (
          <div className="w-100 rounded-2 border bg-white mb-3 container">
            <div className="d-flex align-items-center justify-content-between py-2 px-2">
              <div className="d-flex align-items-center">
                <i className="fa fa-credit-card mr-3" />
                <p className="f-16 text-bid mb-0 poppins-medium">Stripe Subscription</p>
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>
                {stripeBusy ? 'loading…' : (stripeSub.hasStripeCustomer ? `customer ${stripeSub.stripeCustomerId}` : 'no Stripe customer')}
              </div>
            </div>
            {!stripeSub.hasStripeCustomer && (
              <div className="px-3 pb-3" style={{ fontSize: 12, color: '#888' }}>
                This company has no Stripe customer yet. They'll get one when a checkout session completes.
              </div>
            )}
            {stripeSub.hasStripeCustomer && (stripeSub.subscriptions || []).length === 0 && (
              <div className="px-3 pb-3" style={{ fontSize: 12, color: '#856404' }}>
                Customer exists but no active subscriptions. Company is on trial / expired
                {stripeSub.dateOfExpiration && ` until ${new Date(stripeSub.dateOfExpiration).toLocaleDateString()}`}.
              </div>
            )}
            {stripeSub.hasStripeCustomer && (stripeSub.subscriptions || []).length > 0 && (
              <div className="px-3 pb-3">
                {stripeSub.subscriptions.map((s) => (
                  <div key={s.id} style={{ fontSize: 12, marginBottom: 8, padding: 8, background: '#fafbfc', borderRadius: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', color: '#525252' }}>{s.id}</span>
                      <span style={{ padding: '1px 8px', borderRadius: 10, background: s.status === 'active' ? '#e7f5ec' : s.status === 'trialing' ? '#eef2ff' : s.status === 'past_due' ? '#fff4cc' : '#fdecea', color: s.status === 'active' ? '#1e6b3e' : s.status === 'trialing' ? '#15172b' : s.status === 'past_due' ? '#856404' : '#a71d2a' }}>{s.status}</span>
                    </div>
                    {s.currentPeriodEnd && <div style={{ color: '#525252' }}>Renews: {new Date(s.currentPeriodEnd).toLocaleDateString()}{s.cancelAtPeriodEnd ? ' (cancels at period end)' : ''}</div>}
                    {(s.packages || []).length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        Packages: {s.packages.map((p) => (<span key={p} style={{ background: '#eef2ff', padding: '1px 6px', borderRadius: 8, marginRight: 4, fontSize: 11 }}>{p}</span>))}
                      </div>
                    )}
                    {(s.items || []).length > 0 && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
                        Line items: {s.items.map((it, i) => (
                          <span key={i} style={{ marginRight: 8 }}>
                            {it.amount != null && (it.amount / 100).toLocaleString('en-US', { style: 'currency', currency: it.currency || 'usd' })}
                            {it.interval && `/${it.interval}`}
                            {' · '}<code style={{ fontSize: 10 }}>{it.priceId}</code>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PACKAGE TOGGLES — one-click grant/revoke of an entire agent
            (SocialMediaManagement, EmailManagement, ...). Saves into the
            same company Permission row the granular grid below edits;
            saving here overwrites with the union of selected packages. */}
        {allPackages.length > 0 && (
          <div className="w-100 rounded-2 border bg-white mb-3 container">
            <div className="d-flex align-items-center justify-content-between py-2 px-2">
              <div className="d-flex align-items-center">
                <i className="fa fa-cubes mr-3" />
                <p className="f-16 text-bid mb-0 poppins-medium">
                  Licensed Packages
                </p>
              </div>
              <div className="d-flex">
                <button
                  className="btn-header bg-bid text-white border-bid py-1 px-3"
                  disabled={!packagesDirty || savingPackages}
                  onClick={savePackages}
                >
                  <i className="fa fa-save mr-2"></i>
                  {savingPackages ? 'Saving…' : 'Save packages'}
                </button>
              </div>
            </div>
            <div className="px-3 pb-3" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {allPackages.map((pkg) => (
                <label key={pkg} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 10px', border: '1px solid #e5e5e5', borderRadius: 6, background: selectedPackages.includes(pkg) ? '#eef2ff' : '#fff' }}>
                  <input
                    type="checkbox"
                    checked={selectedPackages.includes(pkg)}
                    onChange={() => togglePackage(pkg)}
                  />
                  <span>{pkg}</span>
                </label>
              ))}
            </div>
            <p className="px-3 pb-2" style={{ fontSize: 11, color: '#888' }}>
              Saving here rewrites the company's permission set to the union of
              the selected packages. Granular tweaks below stack on top.
            </p>
          </div>
        )}

        {/* CONNECTOR PLATFORM ALLOWLIST — which social platforms this company's
            Connectors may operate on. Default-disabled except linkedin+facebook;
            the company's own session checkboxes can only narrow this. */}
        {platformCatalog.length > 0 && (
          <div className="w-100 rounded-2 border bg-white mb-3 container">
            <div className="d-flex align-items-center justify-content-between py-2 px-2">
              <div className="d-flex align-items-center">
                <i className="fa fa-plug mr-3" />
                <p className="f-16 text-bid mb-0 poppins-medium">
                  Connector Platforms
                </p>
              </div>
              <div className="d-flex">
                <button
                  className="btn-header bg-bid text-white border-bid py-1 px-3"
                  disabled={!platformsDirty || savingPlatforms}
                  onClick={savePlatforms}
                >
                  <i className="fa fa-save mr-2"></i>
                  {savingPlatforms ? 'Saving…' : 'Save platforms'}
                </button>
              </div>
            </div>
            <div className="px-3 pb-3" style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {platformCatalog.map((plat) => (
                <label key={plat} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 10px', border: '1px solid #e5e5e5', borderRadius: 6, background: allowedPlatforms.includes(plat) ? '#eef2ff' : '#fff', textTransform: 'capitalize' }}>
                  <input
                    type="checkbox"
                    checked={allowedPlatforms.includes(plat)}
                    onChange={() => togglePlatform(plat)}
                  />
                  <span>{plat}</span>
                </label>
              ))}
            </div>
            <p className="px-3 pb-2" style={{ fontSize: 11, color: '#888' }}>
              Gates which platforms the company's Connectors can act on. Disabled by
              default (except LinkedIn &amp; Facebook). The company's per-session
              platform checkboxes can only narrow this set, never widen it.
            </p>
          </div>
        )}

        <div className="w-100 rounded-2 border bg-white mb-3 container">
          <div className="d-flex align-items-center justify-content-between py-2 px-2">
            <div className="d-flex align-items-center">
              <i className="fa fa-lock mr-3" />
              <p className="f-16 text-bid mb-0 poppins-medium">
                Company Permissions
              </p>
            </div>
            <div className="d-flex">
              <button
                className="btn-header bg-bid text-white border-bid py-1 px-3"
                onClick={() => handleSave()}
              >
                <i className="fa fa-save mr-2"></i>
                Save
              </button>
              {/* <button className="btn-header bg-white text-bid border-bid py-1 px-3">
                  <i className="fa fa-times mr-2"></i>
                  Cancel
                </button> */}
            </div>
          </div>
        </div>

        <div className="container mt-1 bg-white p-0 rounded-3 p-2 py-0 border">
          <div className="grid-two-sets">
            {permissions?.map((item, key) => (
              <div className="capitalize d-flex" key={key}>
                <input
                  className="squared-checkbox mr-3"
                  value={item?.permission}
                  onChange={(e) => handleCheckBox(e)}
                  type="checkbox"
                  defaultChecked={item?.checked}
                />
                {item?.permission?.split(/(?=[A-Z])/).join(" ")}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
